import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

export interface TsPaginatorChangeEvent {
  activePage: number;
  pageSize: number;
  totalPages: number;
}

@Component({
  selector: 'ts-paginator',
  templateUrl: './ts-paginator.component.html',
  styleUrls: ['./ts-paginator.component.scss'],
})
export class TsPaginatorComponent extends OnDestroyMixin implements OnInit {
  #totalPages = 1;
  #totalItems = 1;
  #activePage = 1;
  readonly defaultPageSize = 20;
  readonly maxRowButtons = 7;
  readonly pageSizeControl: FormControl<number> = new FormControl(this.defaultPageSize);

  rowButtons: (number | '...')[] = [];

  /** When true, hides the "Items per page" control (fixed page size from {@link pageSize}). */
  @Input() hidePageSize = false;

  /** Options shown in the page size selector. Defaults to 20 / 50 / 100. */
  @Input() pageSizeOptions: number[] = [20, 50, 100];

  @Input() set pageSize(value: number) {
    this.pageSizeControl.setValue(value || this.defaultPageSize, { emitEvent: false });
    this.onPageSizeChanged(value);
  }

  @Input() set totalItems(value: number) {
    this.#totalItems = this.normalizeTotalItems(value);
    this.#totalPages = this.calculateTotalPages(this.#totalItems, this.pageSize);
    this.rowButtons = this.generatePageButtonsRow(this.totalPages, this.activePage);
  }

  @Input() set activePage(value: number) {
    this.#activePage = this.normalizeActivePage(value);
    this.rowButtons = this.generatePageButtonsRow(this.totalPages, this.activePage);
  }

  @Output() pageChanged = new EventEmitter<TsPaginatorChangeEvent>();

  get totalPages(): number {
    return this.#totalPages;
  }

  get activePage(): number {
    return this.#activePage;
  }

  get pageSize(): number {
    return this.pageSizeControl.value || 20;
  }

  ngOnInit(): void {
    this.rowButtons = this.generatePageButtonsRow(this.totalPages, this.activePage);

    this.pageSizeControl.valueChanges.pipe(untilComponentDestroyed(this)).subscribe((value) => {
      const formValue = Number(value);

      this.onPageSizeChanged(formValue);

      this.pageChanged.emit({
        activePage: this.activePage,
        pageSize: formValue,
        totalPages: this.totalPages,
      });
    });
  }

  changeActivePage(page: number): void {
    const nextPage = this.normalizeActivePage(page);

    if (nextPage < 1 || nextPage > this.totalPages) return;

    this.activePage = nextPage;
    this.rowButtons = this.generatePageButtonsRow(this.totalPages, this.activePage);
    this.pageChanged.emit({
      activePage: this.activePage,
      pageSize: this.pageSizeControl.value,
      totalPages: this.totalPages,
    });
  }

  private updateActivePageOnPageSizeChange(): number {
    return this.activePage > this.totalPages ? this.totalPages : this.activePage;
  }

  private calculateTotalPages(totalItems: number, pageSize: number): number {
    const size = this.normalizePageSize(pageSize);

    if (totalItems <= 0) {
      return 1;
    }

    return Math.ceil(totalItems / size);
  }

  private normalizeTotalItems(value: number): number {
    const total = Number(value);

    return Number.isFinite(total) && total >= 0 ? total : 0;
  }

  private normalizeActivePage(value: number): number {
    const page = Number(value);

    return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  }

  private normalizePageSize(value: number): number {
    const size = Number(value);

    return Number.isFinite(size) && size >= 1 ? Math.floor(size) : this.defaultPageSize;
  }

  private onPageSizeChanged(newPageSize: number): void {
    this.#totalPages = this.calculateTotalPages(this.#totalItems, newPageSize);
    this.activePage = this.updateActivePageOnPageSizeChange();
    this.rowButtons = this.generatePageButtonsRow(this.totalPages, this.activePage);
  }

  private generatePageButtonsRow(pagesCount: number, activePage: number): (number | '...')[] {
    const pages = Number.isFinite(pagesCount) && pagesCount >= 1 ? Math.floor(pagesCount) : 1;
    const currentPage = this.normalizeActivePage(activePage);

    if (pages <= 1) return [1];

    if (pages <= this.maxRowButtons) {
      return Array(pages)
        .fill(null)
        .map((_, i) => i + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', pages];
    }

    if (currentPage >= pages - 3) {
      return [1, '...', pages - 4, pages - 3, pages - 2, pages - 1, pages];
    }

    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', pages];
  }
}
